import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/shared";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";

export default function Journey() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: prog, isLoading } = useQuery({ queryKey: ["program"], queryFn: async () => (await api.get("/program")).data });
  const { data: sessions } = useQuery({ queryKey: ["sessions"], queryFn: async () => (await api.get("/sessions")).data });
  if (isLoading || !prog) return <Loader />;

  const currentWeek = user?.current_week || 4;
  const weeks = prog.weeks || [];

  return (
    <div className="space-y-6" data-testid="journey-map">
      <div>
        <h1 className="font-head text-4xl text-csh-blue">My Journey 🗺️</h1>
        <p className="text-muted-foreground">{prog.program?.name} · Follow the path, complete each week!</p>
      </div>

      <div className="relative mx-auto max-w-2xl">
        {weeks.map((w, i) => {
          const status = w.number < currentWeek ? "completed" : w.number === currentWeek ? "active" : "locked";
          const left = i % 2 === 0;
          const wkSessions = (sessions || []).filter((s) => s.week_number === w.number);
          return (
            <motion.div key={w.number} initial={{ opacity: 0, x: left ? -30 : 30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className={`relative mb-4 flex ${left ? "justify-start" : "justify-end"}`}>
              <div data-testid={`week-node-${w.number}`}
                className={`w-full sm:w-[70%] rounded-3xl p-5 shadow-sm border-2 ${
                  status === "active" ? "bg-white border-csh-orange pulse-glow" :
                  status === "completed" ? "bg-white border-green-400" : "bg-black/5 border-black/10 opacity-70"}`}>
                <div className="flex items-center gap-3">
                  <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-2xl ${
                    status === "locked" ? "bg-black/10" : "gradient-purple"}`}>
                    {status === "locked" ? <Icons.Lock className="text-csh-grey/50" /> : <span>{w.emoji}</span>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase text-muted-foreground">Week {w.number}</span>
                      {status === "completed" && <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-700">✓ COMPLETED</span>}
                      {status === "active" && <span className="rounded-full bg-csh-orange px-2 py-0.5 text-[10px] font-bold text-white">ACTIVE</span>}
                      {status === "locked" && <span className="rounded-full bg-black/10 px-2 py-0.5 text-[10px] font-bold text-csh-grey/60">LOCKED</span>}
                    </div>
                    <h3 className="font-head text-xl text-csh-blue truncate">{w.title}</h3>
                    <p className="text-xs text-muted-foreground line-clamp-1">{w.description}</p>
                  </div>
                </div>
                {status !== "locked" && wkSessions.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {wkSessions.map((s) => (
                      <button key={s.id} onClick={() => navigate(`/app/session/${s.id}`)}
                        className="flex items-center gap-1 rounded-full bg-csh-blue/5 px-3 py-1 text-xs font-bold text-csh-blue hover:bg-csh-blue/10">
                        <Icons.Video size={12} /> {s.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
